import { SCENE_INTERACTION } from "../../constants/scene";
import {
	SCENE_EVENTS,
	TRACKING_EVENT_TYPES,
	TRACKING_EVENT_ACTIONS,
} from "../MVC/EVENTS/ModelEvents";
import { tourID } from "../../socket/SocketTour";
import { tourExists } from "../../socket/socketHelper";
import {
	AMPLITUDE_EVENTS,
	CAMERA_MOVE_COOLDOWN_MS,
	getObjectAnalyticsMeta,
	trackAnalytics,
} from "./baglessAnalytics";
import { installUiButtonTracking } from "./uiButtonTracking";
import { getVapiSpaceSessionString } from "./vapiSpaceSession";

class UserTracking {

	constructor({ model }) {

		this.model = model;
		this._worldId = model.config.worldId;
		this._trackingUrl = model.config.trackingUrl;
		this.worldName = model.config.worldName;
		this._temporaryTrackingSessionString = new aptis.UUIDGenerator().generate();
		getVapiSpaceSessionString(this._worldId);
		this._sessionStartedAt = Date.now();
		this._sessionOverSent = false;
		this._enteredRoomTracked = false;
		this._lastCameraMoveAt = 0;
		this._vapiStartedAt = {};
		this._vapiCommandN = 0;
		this._vapiAnswerN = 0;
		this._vapiToolN = 0;
		this._vapiTalkN = 0;
		this._lastVapiCommand = { text: '', at: 0 };
		this._hoverTimer = null;
		this._pendingHover = null;
		this._activeHover = null;
		this._sessionStats = {
			camera_moves: 0,
			camera_hovers: 0,
			vapi_calls: 0,
			vapi_commands: 0,
			vapi_tools: 0,
			vapi_talks: 0,
			vapi_answers: 0,
			seats_taken: 0,
			scenes_changed: 0,
			input_fields_opened: 0,
			input_fields_submitted: 0,
			funnel_steps: 0,
		};

		this.trackStartWorld();

		this.initListeners()
	}

	getSessionString() {
		return this._temporaryTrackingSessionString;
	}

	getVapiSpaceSessionString() {
		return getVapiSpaceSessionString(this._worldId);
	}

	initListeners() {
		this.model.addEventListener(SCENE_EVENTS.SCENE_CHANGED, (e) => {
			const { data: { scene } } = e;
			this.trackChangeScene(scene);
		});

		this.model.addEventListener(SCENE_INTERACTION.USER_TAKES_SEAT, (e) => {
			const { data } = e;
			this.trackTakeASeat(data);
		});

		this.model.addEventListener(SCENE_INTERACTION.USER_LEAVES_SEAT, (e) => {
			this.trackLeaveSeat(e?.data);
		});

		this.model.addEventListener(SCENE_EVENTS.GUIDED_MODE_START, (e) => {
			const { data } = e;
			this.trackTourStart(data);
		});

		this.model.addEventListener(SCENE_EVENTS.TOUR_MODE_STARTED, (e) => {
			this.trackTourJoin();
		});

		this.model.addEventListener(SCENE_EVENTS.TOUR_MODE_ENDED, (e) => {
			this.trackTourEnded();
		});

		this.model.addEventListener('vapi-agent-call-start', (e) => {
			this.trackVapiConnected(e?.data);
		});
		this.model.addEventListener('vapi-agent-status', (e) => {
			const state = e?.data?.state;
			if (state === 'request-media' || state === 'connecting') {
				this.trackVapiStart(e?.data);
			}
		});
		this.model.addEventListener('vapi-agent-call-end', (e) => {
			this.trackVapiEnd(e?.data);
		});
		this.model.addEventListener('vapi-agent-transcript', (e) => {
			this.trackVapiTranscript(e?.data);
		});
		this.model.addEventListener('vapi-agent-command', (e) => {
			this.trackVapiCommand(e?.data, e?.data?.source || 'command');
		});
		this.model.addEventListener('vapi-agent-tool-call', (e) => {
			this.trackVapiToolCall(e?.data);
		});
		this.model.addEventListener('input-field-opened', (e) => {
			this.trackInputFieldOpened(e?.data);
		});
		this.model.addEventListener('input-field-submitted', (e) => {
			this.trackInputFieldSubmitted(e?.data);
		});
		this.model.addEventListener('input-field-validation-failed', (e) => {
			this.trackInputFieldValidationFailed(e?.data);
		});
		this.model.addEventListener('funnel-step', (e) => {
			this.trackFunnelStep(e?.data);
		});
		this.model.addEventListener('session-data-stored', (e) => {
			this.trackSessionDataStored(e?.data);
		});


		installUiButtonTracking(this.model);

		const controls = this.model.view?.controls;
		if (controls?.addEventListener) {
			controls.addEventListener('start', () => this.trackCameraMove());
		}

		this._onPageHide = () => this.trackSessionOver();
		window.addEventListener('pagehide', this._onPageHide);
		window.addEventListener('beforeunload', this._onPageHide);
	}

	_getUserName() {
		if (this.model.config.email) {
			return `${this.model.config.userName},${this.model.config.userId}`;
		}
		return 'Anonymous';
	}

	_trackAmplitude(eventName, extra = {}) {
		trackAnalytics(this.model, eventName, extra);
	}

	_clearHoverTimer() {
		if (this._hoverTimer) {
			clearTimeout(this._hoverTimer);
			this._hoverTimer = null;
		}
	}

	trackStartWorld() {
		const data = {
			'sessionString': this._temporaryTrackingSessionString,
			'TrackingLogItem': {
				'worldId': this._worldId,
				'itemId': null,
				'itemType': TRACKING_EVENT_TYPES.WORLD,
				'action': TRACKING_EVENT_ACTIONS.START_WORLD,
				'text': `User ${this._getUserName()} has entered the room`,
			}
		};

		this.model.saveWorldTracking(data);
	}

	onRoomEntered({ viaSceneChange } = {}) {
		if (viaSceneChange || this._enteredRoomTracked) return;
		this._enteredRoomTracked = true;
		this._trackAmplitude(AMPLITUDE_EVENTS.ENTERED_ROOM);
	}

	trackIntro(eventType) {
		if (eventType === 'introAnimationStarted') {
			this._trackAmplitude(AMPLITUDE_EVENTS.INTRO_STARTED);
		} else if (eventType === 'introAnimationEnded') {
			this._trackAmplitude(AMPLITUDE_EVENTS.INTRO_ENDED);
		}
	}

	trackCameraMove() {
		const now = Date.now();
		if (now - this._lastCameraMoveAt < CAMERA_MOVE_COOLDOWN_MS) return;
		this._lastCameraMoveAt = now;
		this._sessionStats.camera_moves += 1;
		this._trackAmplitude(AMPLITUDE_EVENTS.MOVED_CAMERA, {
			move_n: this._sessionStats.camera_moves,
		});
	}

	trackCameraOverObject(sourceId, hitObject) {
		this._clearHoverTimer();
		const meta = getObjectAnalyticsMeta(this.model.view, sourceId, hitObject);
		if (!meta) return;
		this._pendingHover = { sourceId: meta.object_id, meta, startedAt: Date.now() };
		this._activeHover = this._pendingHover;
		this._sessionStats.camera_hovers += 1;
		const elementType = meta.element_type || meta.object_type || 'unknown';
		this._trackAmplitude(`Camera over object ${elementType}`, {
			...meta,
			element_type: elementType,
			hover_n: this._sessionStats.camera_hovers,
		});
	}

	trackCameraLeftObject(sourceId) {
		this._clearHoverTimer();
		const idStr = sourceId != null ? String(sourceId) : null;
		const active = this._activeHover;
		this._pendingHover = null;
		this._activeHover = null;
		if (!active || active.sourceId !== idStr) return;
		const dwell_ms = Date.now() - active.startedAt;
		const meta = active.meta || getObjectAnalyticsMeta(this.model.view, sourceId) || {};
		const elementType = meta.element_type || meta.object_type || 'unknown';
		this._trackAmplitude(`Camera left object ${elementType}`, {
			...meta,
			element_type: elementType,
			dwell_ms,
			dwell_seconds: Math.round(dwell_ms / 1000),
			hover_n: this._sessionStats.camera_hovers,
		});
	}

	_vapiBaseProps(data, extra = {}) {
		const agentId = data?.vapiAgentId != null ? String(data.vapiAgentId) : null;
		const meta = getObjectAnalyticsMeta(this.model.view, agentId) || {};
		return {
			...meta,
			object_id: agentId || meta.object_id,
			element_type: 'vraiAgent',
			object_type: 'vraiAgent',
			object_label: 'VAPI agent',
			is_vapi: true,
			object_name: data?.vapiAgentName || meta.object_name,
			...extra,
		};
	}

	trackVapiStart(data) {
		const agentId = data?.vapiAgentId != null ? String(data.vapiAgentId) : null;
		if (agentId && this._vapiStartedAt[agentId]) return;
		if (agentId) this._vapiStartedAt[agentId] = Date.now();
		this._vapiCommandN = 0;
		this._vapiAnswerN = 0;
		this._vapiToolN = 0;
		this._vapiTalkN = 0;
		this._sessionStats.vapi_calls += 1;
		this._trackAmplitude(AMPLITUDE_EVENTS.STARTED_VAPI, this._vapiBaseProps(data));
	}

	trackVapiConnected(data) {
		const agentId = data?.vapiAgentId != null ? String(data.vapiAgentId) : null;
		if (agentId && !this._vapiStartedAt[agentId]) this._vapiStartedAt[agentId] = Date.now();
		this._trackAmplitude(AMPLITUDE_EVENTS.VAPI_CONNECTED, this._vapiBaseProps(data));
	}

	trackVapiEnd(data) {
		const agentId = data?.vapiAgentId != null ? String(data.vapiAgentId) : null;
		const startedAt = agentId ? this._vapiStartedAt[agentId] : null;
		if (agentId) delete this._vapiStartedAt[agentId];
		const duration_ms = startedAt ? Date.now() - startedAt : 0;
		let reason = 'ended';
		if (data?.sceneChange) reason = 'scene_change';
		else if (data?.userEnded) reason = 'user_ended';
		else if (data?.failed) reason = 'failed';
		else if (data?.cancelled) reason = 'cancelled';
		this._trackAmplitude(AMPLITUDE_EVENTS.ENDED_VAPI, this._vapiBaseProps(data, {
			duration_ms,
			duration_seconds: Math.round(duration_ms / 1000),
			duration_minutes: Math.round((duration_ms / 60000) * 10) / 10,
			reason,
			command_n: this._vapiCommandN,
			tool_n: this._vapiToolN,
			talk_n: this._vapiTalkN,
			answer_n: this._vapiAnswerN,
		}));
	}

	trackVapiTranscript(data) {
		if (!data?.isFinal) return;
		const role = data.role || '';
		const source = data.source || '';
		if (role === 'user' && source === 'command') return;
		if (role === 'user') {
			this.trackVapiTalked(data);
			return;
		}
		if (role === 'assistant') {
			this.trackVapiAnswered(data);
		}
	}

	trackVapiCommand(data, source = 'command') {
		this._vapiCommandN += 1;
		this._sessionStats.vapi_commands += 1;
		const command = String(data?.transcript || data?.command || data?.text || '').trim();
		this._lastVapiCommand = { text: command, at: Date.now() };
		this._trackAmplitude(AMPLITUDE_EVENTS.SENT_VAPI_COMMAND, this._vapiBaseProps(data, {
			command_source: source,
			command_text: command ? command.slice(0, 120) : undefined,
			command_n: this._vapiCommandN,
		}));
	}

	trackVapiToolCall(data) {
		this._vapiToolN += 1;
		this._sessionStats.vapi_tools += 1;
		const toolName = String(data?.toolName || data?.name || '').trim();
		const extra = {
			tool_name: toolName || undefined,
			tool_call_id: data?.toolCallId || undefined,
			tool_n: this._vapiToolN,
		};
		// Never send private session payloads (emails, summaries, etc.) to Amplitude.
		if (toolName !== 'storeSessionData') {
			extra.tool_arguments = data?.toolArguments || undefined;
		} else {
			extra.field_key = data?.fieldKey || undefined;
			extra.funnel_step = data?.funnelStep || undefined;
		}
		this._trackAmplitude(AMPLITUDE_EVENTS.VAPI_CALLED_TOOL, this._vapiBaseProps(data, extra));
	}

	_inputFieldBaseProps(data = {}) {
		return {
			object_id: data.objectId != null ? String(data.objectId) : undefined,
			element_type: 'inputField',
			object_type: 'inputField',
			object_label: 'Input field',
			object_name: data.objectName || undefined,
			field_key: data.fieldKey || undefined,
			funnel_step: data.funnelStep || undefined,
			validation: data.validation || undefined,
			source: data.source || undefined,
			amplitude_session_id: data.amplitudeSessionId || undefined,
		};
	}

	trackInputFieldOpened(data) {
		this._sessionStats.input_fields_opened += 1;
		this._trackAmplitude(AMPLITUDE_EVENTS.OPENED_INPUT_FIELD, {
			...this._inputFieldBaseProps(data),
			open_n: this._sessionStats.input_fields_opened,
		});
	}

	trackInputFieldSubmitted(data) {
		this._sessionStats.input_fields_submitted += 1;
		this._trackAmplitude(AMPLITUDE_EVENTS.SUBMITTED_INPUT_FIELD, {
			...this._inputFieldBaseProps(data),
			submit_n: this._sessionStats.input_fields_submitted,
		});
	}

	trackInputFieldValidationFailed(data) {
		this._trackAmplitude(AMPLITUDE_EVENTS.INPUT_FIELD_VALIDATION_FAILED, {
			...this._inputFieldBaseProps(data),
			error: data?.error || undefined,
		});
	}

	trackFunnelStep(data) {
		this._sessionStats.funnel_steps += 1;
		this._trackAmplitude(AMPLITUDE_EVENTS.FUNNEL_STEP, {
			...this._inputFieldBaseProps(data),
			step_n: this._sessionStats.funnel_steps,
		});
	}

	trackSessionDataStored(data) {
		if (data?.source === 'vapi') {
			this._trackAmplitude(AMPLITUDE_EVENTS.VAPI_STORED_SESSION_DATA, this._inputFieldBaseProps(data));
		}
		if (data?.funnelStep) {
			this.trackFunnelStep(data);
		}
	}

	trackVapiTalked(data) {
		const text = String(data?.transcript || '').trim();
		if (
			text
			&& this._lastVapiCommand.text === text
			&& (Date.now() - this._lastVapiCommand.at) < 8000
		) {
			return;
		}
		this._vapiTalkN += 1;
		this._sessionStats.vapi_talks += 1;
		this._trackAmplitude(AMPLITUDE_EVENTS.TALKED_WITH_VAPI, this._vapiBaseProps(data, {
			talk_text: text ? text.slice(0, 120) : undefined,
			talk_n: this._vapiTalkN,
		}));
	}

	trackVapiAnswered(data) {
		this._vapiAnswerN += 1;
		this._sessionStats.vapi_answers += 1;
		const text = String(data?.transcript || '').trim();
		this._trackAmplitude(AMPLITUDE_EVENTS.VAPI_ANSWERED_USER, this._vapiBaseProps(data, {
			answer_text: text ? text.slice(0, 120) : undefined,
			answer_length: text.length,
			answer_n: this._vapiAnswerN,
		}));
	}

	trackChangeScene(scene) {
		const data = {
			'sessionString': this._temporaryTrackingSessionString,
			'TrackingLogItem': {
				'worldId': this._worldId,
				'itemId': scene.id,
				'itemType': TRACKING_EVENT_TYPES.SCENE,
				'action': TRACKING_EVENT_ACTIONS.CHANGE_SCENE,
				'text': `User ${this._getUserName()} has change location to ${scene.publicName}`
			}
		};

		this.model.saveWorldTracking(data);
		this._clearHoverTimer();
		this._pendingHover = null;
		this._activeHover = null;
		this._sessionStats.scenes_changed += 1;
		this._trackAmplitude(AMPLITUDE_EVENTS.CHANGED_SCENE, {
			scene_id: scene?.id,
			scene_name: scene?.publicName || scene?.name,
			from_scene_id: this.model.view?.historyLastLocation || null,
		});
	}

	trackTakeASeat(seat) {
		const data = {
			'sessionString': this._temporaryTrackingSessionString,
			'TrackingLogItem': {
				'worldId': this._worldId,
				'itemId': seat.id,
				'itemType': TRACKING_EVENT_TYPES.GUEST_SEAT,
				'action': TRACKING_EVENT_ACTIONS.TAKE_A_SEAT,
				'text': `User ${this._getUserName()} took a seat`
			}
		};

		this.model.saveWorldTracking(data);
		this._sessionStats.seats_taken += 1;
		this._trackAmplitude(AMPLITUDE_EVENTS.TOOK_SEAT, {
			object_id: seat?.id,
			object_type: 'guestSeat',
		});
	}

	trackLeaveSeat(seat) {
		this._trackAmplitude(AMPLITUDE_EVENTS.LEFT_SEAT, {
			object_id: seat?.id,
			object_type: 'guestSeat',
		});
	}

	trackTourStart(tour) {
		return;
		this.tourTimer = Date.now()

		const data = {
			'sessionString': this._temporaryTrackingSessionString,
			'TrackingLogItem': {
				'worldId': this._worldId,
				'itemId': null,
				'itemType': TRACKING_EVENT_TYPES.WORLD,
				'action': TRACKING_EVENT_ACTIONS.START_TOUR,
				'text': `User ${this._getUserName()} started a Tour ${tour.inputValue}`
			}
		};

		this.model.saveWorldTracking(data);
	}

	trackTourJoin() {
		return;
		const tourStarted = tourExists(tourID, this.model.tourData);
		if (!tourStarted) {
			return;
		}

		this.tourTimer = Date.now()
		const tourName = this.model.tourData[tourID].name;

		const data = {
			'sessionString': this._temporaryTrackingSessionString,
			'TrackingLogItem': {
				'worldId': this._worldId,
				'itemId': null,
				'itemType': TRACKING_EVENT_TYPES.WORLD,
				'action': TRACKING_EVENT_ACTIONS.JOIN_TOUR,
				'text': `User ${this._getUserName()} joined Tour ${tourName}`,
			}
		};

		this.model.saveWorldTracking(data);
	}

	trackTourEnded() {
		if (!this.tourTimer) {
			return;
		}
		const duration = Math.ceil((Date.now() - this.tourTimer) / 60000)

		// set null to prevent sending multiple times
		this.tourTimer = null

		const data = {
			'sessionString': this._temporaryTrackingSessionString,
			'TrackingLogItem': {
				'worldId': this._worldId,
				'itemId': null,
				'itemType': TRACKING_EVENT_TYPES.WORLD,
				'action': TRACKING_EVENT_ACTIONS.END_TOUR,
				'text': `Tour ended after ${duration} Mins`
			}
		};

		this.model.saveWorldTracking(data);
	}

	trackSessionOver() {
		if (this._sessionOverSent) return;
		this._sessionOverSent = true;
		this._clearHoverTimer();
		const duration_ms = Date.now() - this._sessionStartedAt;
		this._trackAmplitude(AMPLITUDE_EVENTS.SESSION_OVER, {
			duration_ms,
			duration_seconds: Math.round(duration_ms / 1000),
			duration_minutes: Math.round((duration_ms / 60000) * 10) / 10,
			...this._sessionStats,
		});
	}
}

export { UserTracking }
